import useGetCurriculumOptions from "@modules/shared/selectors/hooks/useGetCurriculumOptions";
import useGetModalityOptions from "@modules/shared/selectors/hooks/useGetModalityOptions";
import useGetProgramOptions from "@modules/shared/selectors/hooks/useGetProgramOptions";
import useGetCustomModelValues from "@services/collections/custom-models/customModelsServices";

interface useValueSelectorFactoryProps {
  propertyType?: string;
  scenario?: string;
  origin?: string;
  source?: string;
  customModelCode?: string;
  ignoreAccessFilters?: boolean;
}

export const PROPERTY_TYPES = {
  modality: {
    type: "modality",
    key: "darwined.ai/modality/codigo_cliente",
    source: "NATIVE_CATALOGUE",
    nameI18n: "group-restrictions.manage-restrictions.form.properties.modality",
  },
  program: {
    type: "program",
    key: "darwined.ai/programs/code",
    source: "NATIVE_CATALOGUE",
    nameI18n: "group-restrictions.manage-restrictions.form.properties.program",
  },
  curriculum: {
    type: "curriculum",
    key: "darwined.ai/curriculums/plan",
    source: "NATIVE_CATALOGUE",
    nameI18n: "group-restrictions.manage-restrictions.form.properties.curriculum",
  },
  class: {
    key: "itesm.mx/studreg_class",
    type: "class",
    source: "CUSTOM_MODEL",
    nameI18n: "group-restrictions.manage-restrictions.form.properties.class",
  },
  student_attributes: {
    key: "itesm.mx/studreg_student_attributes",
    type: "student_attributes",
    source: "CUSTOM_MODEL",
    nameI18n: "group-restrictions.manage-restrictions.form.properties.student_attributes",
  },
  cohorts: {
    key: "itesm.mx/studreg_cohorts",
    type: "cohorts",
    source: "CUSTOM_MODEL",
    nameI18n: "group-restrictions.manage-restrictions.form.properties.cohorts",
  },
};

const useValueSelectorFactory = ({
  propertyType,
  scenario,
  source,
  customModelCode,
  origin,
  ignoreAccessFilters,
}: useValueSelectorFactoryProps) => {
  const { loadOptions: loadModalityOptions } = useGetModalityOptions({
    scenario,
    key: propertyType,
    ignoreAccessFilters,
  });

  const { loadCurriculumOptions } = useGetCurriculumOptions({
    scenario,
    key: propertyType,
    ignoreAccessFilters,
  });

  const { loadProgramOptions } = useGetProgramOptions({
    scenario,
    key: propertyType,
    ignoreAccessFilters,
  });

  const { loadCustomModelValues } = useGetCustomModelValues({
    scenario,
    origin,
    customModelCode,
    key: propertyType,
    ignoreAccessFilters,
  });

  const getValueSelectorMethod = () => {
    const loadOptionsType = {
      [PROPERTY_TYPES["modality"].key]: loadModalityOptions,
      [PROPERTY_TYPES["curriculum"].key]: loadCurriculumOptions,
      [PROPERTY_TYPES["program"].key]: loadProgramOptions,
    };
    const isCustomModel = source === "CUSTOM_MODEL";

    if (!isCustomModel && !loadOptionsType?.[propertyType]) {
      return null;
    }

    return {
      loadOptions: isCustomModel ? loadCustomModelValues : loadOptionsType?.[propertyType],
      isPaginated: true,
    };
  };

  return {
    valueSelectorFactory: getValueSelectorMethod(),
  };
};

export default useValueSelectorFactory;
